import { useEffect, useMemo, useRef, useState } from 'react';
import type { PrintTemplateDocument } from '#shared/types';
import { useDebounced } from '#client/hooks/useDebounced';
import { ApiError, api } from '#client/lib/api';
import { ENDPOINT } from '#client/lib/endpoints';

interface PdfPreview {
  /** An object URL for the rendered document, or undefined before the first one. */
  url?: string;
  error?: string;
  /** A render is in flight. The previous document stays on screen meanwhile. */
  pending: boolean;
}

/** Long enough to swallow a drag on a slider, short enough to feel live. */
const SETTLE_MS = 300;

/**
 * Renders a draft template, by asking the server to print it.
 *
 * The preview comes from `POST /print/preview`, which runs the same renderer as
 * a real print job against the same sample data — so this is not a preview *of*
 * the output, it is the output. There is no second layout implementation that
 * could drift, which is the failure every WYSIWYG editor eventually has.
 *
 * Two things here are load-bearing:
 *
 *   * the previous object URL is revoked only once its replacement has arrived.
 *     Revoking in the effect's cleanup — the obvious placement — tears down the
 *     URL the iframe is still displaying the instant an input changes, so the
 *     preview blinks empty on every keystroke;
 *   * requests are aborted when the draft moves on, so a slow render cannot
 *     land after a faster newer one and show a document that is two edits old.
 */
export function usePdfPreview(document: PrintTemplateDocument): PdfPreview {
  const settled = useDebounced(document, SETTLE_MS);
  // Keyed by the serialised document: the draft object is rebuilt on every
  // keystroke, so its identity says nothing about whether it changed.
  const body = JSON.stringify({ document: settled });
  const payload = useMemo<unknown>(() => JSON.parse(body), [body]);

  const [state, setState] = useState<{ url?: string; error?: string }>({});
  const shown = useRef<string>(undefined);

  useEffect(() => {
    const controller = new AbortController();

    api
      .postForBlob(ENDPOINT.printPreview, payload, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        const next = URL.createObjectURL(blob);
        if (shown.current) URL.revokeObjectURL(shown.current);
        shown.current = next;
        setState({ url: next });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setState((current) => ({
          ...current,
          error: cause instanceof ApiError ? cause.message : 'Could not render this template',
        }));
      });

    return () => controller.abort();
  }, [payload]);

  // Only on unmount: the in-flight cleanup above must not touch the URL the
  // iframe is showing.
  useEffect(
    () => () => {
      if (shown.current) URL.revokeObjectURL(shown.current);
    },
    [],
  );

  return { ...state, pending: JSON.stringify({ document }) !== body };
}
